// Shared, client-safe helpers for the Pal Stars engagement systems:
// daily login streaks, the daily Pal puzzle and the weekly leaderboard.
// No server-only imports live here, so both the browser and the server
// functions can use the same reward maths and share formatting.
import { z } from "zod";

/** Streak day that pays the big milestone bonus (mirrors claim_login_streak). */
export const STREAK_MILESTONE = 7;

/** Packs + spins paid for landing on `streak` — mirrors the SQL reward maths. */
export function streakRewards(streak: number): { packs: number; spins: number; milestone: boolean } {
  const step = Math.floor((Math.max(1, streak) - 1) / 3);
  let packs = Math.min(5, 1 + step);
  let spins = Math.min(10, 2 + step);
  const milestone = streak > 0 && streak % STREAK_MILESTONE === 0;
  if (milestone) {
    packs += 5;
    spins += 10;
  }
  return { packs, spins, milestone };
}

/** Preview ladder shown on the page so the next reward is always visible. */
export function streakLadder(): { day: number; packs: number; spins: number; milestone: boolean }[] {
  return Array.from({ length: 7 }, (_, i) => ({ day: i + 1, ...streakRewards(i + 1) }));
}

/* ------------------------------- Pal puzzle ------------------------------ */

/** Guesses allowed per daily puzzle. */
export const PUZZLE_MAX_GUESSES = 6;

/** Spins paid for solving the day's puzzle (flat, so it cannot be gamed). */
export const PUZZLE_SPINS = 5;

/** Day 1 of the daily Pal puzzle (UTC). */
export const PUZZLE_EPOCH = "2026-01-01";

/** Human puzzle number, e.g. "Pal Puzzle #239". */
export function puzzleNumber(day: string): number {
  const a = Date.parse(`${PUZZLE_EPOCH}T00:00:00Z`);
  const b = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 1;
  return Math.max(1, Math.floor((b - a) / 86_400_000) + 1);
}

export type Hint = "hit" | "close" | "miss" | "up" | "down";

export interface PuzzleClue {
  label: string;
  value: string;
  hint: Hint;
}

export interface PuzzleGuess {
  name: string;
  slug: string;
  correct: boolean;
  clues: PuzzleClue[];
}

export const puzzleGuessSchema = z.object({
  guess: z.string().trim().min(1).max(60),
  attempt: z.number().int().min(1).max(PUZZLE_MAX_GUESSES),
});

const HINT_EMOJI: Record<Hint, string> = {
  hit: "🟩",
  close: "🟨",
  miss: "⬛",
  up: "🔼",
  down: "🔽",
};

/** Emoji row for one guess — the shareable, spoiler-free result grid. */
export function guessRow(g: PuzzleGuess): string {
  return g.clues.map((c) => HINT_EMOJI[c.hint]).join("");
}

/** Share text for social posts: spoiler-free and links back to the puzzle. */
export function puzzleShareText(day: string, guesses: PuzzleGuess[], solved: boolean): string {
  const score = solved ? `${guesses.length}/${PUZZLE_MAX_GUESSES}` : `X/${PUZZLE_MAX_GUESSES}`;
  return [
    `Palworld Pal Puzzle #${puzzleNumber(day)} ${score}`,
    ...guesses.map(guessRow),
    "https://palstars.com/daily-puzzle",
  ].join("\n");
}
